import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { SERVICES } from "../lib/services";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, Upload, X, ArrowRight, ArrowLeft, Copy, Check } from "lucide-react";

const Booking = () => {
    const { t, lang } = useApp();
    const { user } = useAuth();
    const [params] = useSearchParams();
    const nav = useNavigate();

    const [step, setStep] = useState(1);
    const [service, setService] = useState(params.get("service") || "");
    const [form, setForm] = useState({ full_name: "", phone: "", email: "", address: "", notes: "" });
    const [files, setFiles] = useState([]); // {url, filename}
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (user && user !== false) {
            setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
        }
    }, [user]);

    const selectedSvc = SERVICES.find((s) => s.id === service);

    const handleFile = async (e) => {
        const list = Array.from(e.target.files || []);
        if (!list.length) return;
        setUploading(true);
        try {
            for (const f of list) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setFiles((prev) => [...prev, data]);
            }
            toast.success("Uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!service || !form.full_name || !form.phone) {
            toast.error("Please fill required fields");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/bookings", {
                service_id: service,
                service_name: selectedSvc?.name.en || service,
                ...form,
                document_urls: files.map((f) => f.url),
            });
            setResult(data);
            toast.success(t.booking.success);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const copyRef = () => {
        navigator.clipboard.writeText(result.reference);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (result) {
        return (
            <div data-testid="booking-success" className="container-x section-pad max-w-2xl">
                <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 text-center fade-up">
                    <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center bg-[hsl(var(--gold))]/20 text-[hsl(var(--royal))] mb-6">
                        <CheckCircle2 size={40} />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold">{t.booking.success}</h2>
                    <p className="mt-3 text-muted-foreground">{t.booking.reference}</p>
                    <div className="mt-6 inline-flex items-center gap-3 rounded-full bg-muted/70 border border-border px-6 py-3">
                        <span data-testid="booking-reference" className="font-mono font-bold text-lg text-[hsl(var(--royal))]">{result.reference}</span>
                        <button onClick={copyRef} className="text-muted-foreground hover:text-[hsl(var(--royal))]">
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link data-testid="go-track" to={`/track?ref=${result.reference}`} className="btn-royal">Track Application <ArrowRight size={16} /></Link>
                        <button onClick={() => { setResult(null); setStep(1); setService(""); setFiles([]); }} className="btn-ghost-royal">Book Another</button>
                    </div>
                </div>
            </div>
        );
    }

    const StepDot = ({ n, active, done }) => (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
            done ? "bg-[hsl(var(--royal))] text-white border-[hsl(var(--royal))]" :
            active ? "border-[hsl(var(--royal))] text-[hsl(var(--royal))] bg-background" :
            "border-border text-muted-foreground bg-background"
        }`}>{done ? <Check size={14} /> : n}</div>
    );

    return (
        <div data-testid="page-booking" className="container-x section-pad">
            <div className="max-w-3xl mx-auto">
                <div className="chip bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] mb-4">{t.nav.booking}</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.booking.title}</h1>
                <p className="mt-3 text-muted-foreground">{t.booking.desc}</p>

                <div className="mt-10 flex items-center gap-3 sm:gap-4">
                    {[t.booking.step1, t.booking.step2, t.booking.step3, t.booking.step4].map((label, i) => (
                        <React.Fragment key={i}>
                            <div className="flex items-center gap-2">
                                <StepDot n={i + 1} active={step === i + 1} done={step > i + 1} />
                                <span className={`text-xs sm:text-sm font-medium hidden sm:inline ${step === i + 1 ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
                            </div>
                            {i < 3 && <div className="flex-1 h-px bg-border" />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="mt-8 rounded-3xl border border-border bg-card p-6 sm:p-8">
                    {step === 1 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t.booking.step1}</h3>
                            <div className="grid sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-2">
                                {SERVICES.map((s) => (
                                    <button key={s.id} data-testid={`svc-opt-${s.id}`} onClick={() => setService(s.id)}
                                        className={`text-left p-4 rounded-2xl border-2 transition-colors ${
                                            service === s.id ? "border-[hsl(var(--royal))] bg-[hsl(var(--royal))]/5" : "border-border hover:border-[hsl(var(--royal))]/50"
                                        }`}>
                                        <div className="font-semibold">{s.name[lang]}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{s.category}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-6 flex justify-end">
                                <button data-testid="booking-next-1" disabled={!service} onClick={() => setStep(2)} className="btn-royal disabled:opacity-40 disabled:cursor-not-allowed">
                                    {t.booking.next} <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t.booking.step2}</h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field data-testid="booking-name" label={t.booking.fullName} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
                                <Field data-testid="booking-phone" label={t.booking.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                                <Field data-testid="booking-email" label={t.booking.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                                <Field data-testid="booking-address" label={t.booking.address} value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
                                <div className="sm:col-span-2">
                                    <label className="text-sm font-medium">{t.booking.notes}</label>
                                    <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                                        className="mt-1.5 w-full rounded-xl border-2 border-border bg-background px-4 py-3 focus:border-[hsl(var(--royal))] focus:outline-none" />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-between">
                                <button onClick={() => setStep(1)} className="btn-ghost-royal"><ArrowLeft size={16} /> {t.booking.back}</button>
                                <button data-testid="booking-next-2" disabled={!form.full_name || !form.phone} onClick={() => setStep(3)} className="btn-royal disabled:opacity-40 disabled:cursor-not-allowed">
                                    {t.booking.next} <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-1">{t.booking.uploadTitle}</h3>
                            <p className="text-sm text-muted-foreground mb-4">{t.booking.uploadDesc}</p>
                            <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-border hover:border-[hsl(var(--royal))] p-8 text-center transition-colors">
                                <Upload size={28} className="mx-auto text-[hsl(var(--royal))]" />
                                <div className="mt-2 text-sm font-medium">Click to upload</div>
                                <div className="text-xs text-muted-foreground">PDF, JPG, PNG, WEBP (max 8MB each)</div>
                                <input data-testid="booking-file-input" type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFile} />
                            </label>
                            {uploading && <p className="mt-3 text-sm text-muted-foreground">Uploading…</p>}
                            {files.length > 0 && (
                                <ul className="mt-4 space-y-2">
                                    {files.map((f, i) => (
                                        <li key={i} className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-4 py-2 text-sm">
                                            <span className="truncate">{f.filename}</span>
                                            <button onClick={() => setFiles(files.filter((_, k) => k !== i))} className="text-muted-foreground hover:text-destructive"><X size={16} /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-6 flex justify-between">
                                <button onClick={() => setStep(2)} className="btn-ghost-royal"><ArrowLeft size={16} /> {t.booking.back}</button>
                                <button data-testid="booking-next-3" onClick={() => setStep(4)} className="btn-royal">{t.booking.next} <ArrowRight size={16} /></button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t.booking.step4}</h3>
                            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                                <Info label={t.booking.service} value={selectedSvc?.name[lang]} />
                                <Info label={t.booking.fullName} value={form.full_name} />
                                <Info label={t.booking.phone} value={form.phone} />
                                <Info label={t.booking.email} value={form.email || "—"} />
                                <Info label={t.booking.address} value={form.address || "—"} className="sm:col-span-2" />
                                <Info label={t.booking.notes} value={form.notes || "—"} className="sm:col-span-2" />
                                <Info label="Documents" value={`${files.length} file(s)`} />
                            </dl>
                            <div className="mt-6 flex justify-between">
                                <button onClick={() => setStep(3)} className="btn-ghost-royal"><ArrowLeft size={16} /> {t.booking.back}</button>
                                <button data-testid="booking-submit" disabled={submitting} onClick={submit} className="btn-royal disabled:opacity-40">
                                    {submitting ? "Submitting…" : t.booking.submit} <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, value, onChange, type = "text", required, ...rest }) => (
    <div>
        <label className="text-sm font-medium">{label}{required && <span className="text-destructive">*</span>}</label>
        <input {...rest} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
            className="mt-1.5 w-full rounded-xl border-2 border-border bg-background px-4 py-3 focus:border-[hsl(var(--royal))] focus:outline-none" />
    </div>
);
const Info = ({ label, value, className = "" }) => (
    <div className={`rounded-xl bg-muted/40 border border-border p-3 ${className}`}>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-0.5 font-medium break-words">{value}</div>
    </div>
);

export default Booking;
